package org.teddapp.project.ui.notes

/**
 * Les liens entre notes, écrits `[[Titre de la note]]`. Un lien désigne une note par son
 * titre, sans tenir compte de la casse ni des espaces au bord.
 */
internal object NoteLinks {

    /** Un lien complet, et son titre en groupe 1. */
    val link = Regex("\\[\\[([^\\[\\]\\n]+)\\]\\]")

    /** Un lien qu'on est en train d'écrire : `[[` puis le début du titre, jusqu'au curseur. */
    private val typing = Regex("\\[\\[([^\\[\\]\\n]*)$")

    /** La clé de comparaison d'un titre. */
    fun key(title: String) = title.trim().lowercase()

    /** Les titres cités par un texte, dans l'ordre, sans doublon. */
    fun targets(text: String): List<String> =
        link.findAll(text).map { it.groupValues[1].trim() }.filter { it.isNotEmpty() }.distinctBy(::key).toList()

    /** Vrai si [text] cite la note de titre [title]. */
    fun mentions(text: String, title: String): Boolean {
        val k = key(title)
        return k.isNotEmpty() && link.findAll(text).any { key(it.groupValues[1]) == k }
    }

    /** Le début de titre tapé après `[[` juste avant [cursor], ou null si on n'écrit pas de lien. */
    fun typedQuery(text: String, cursor: Int): String? {
        val c = cursor.coerceIn(0, text.length)
        return typing.find(text.substring(maxOf(0, c - 120), c))?.groupValues?.get(1)
    }

    /**
     * Complète le lien en cours avec [title] : le début tapé est remplacé, les crochets
     * fermants posés s'ils manquent, le curseur placé après.
     */
    fun complete(text: String, cursor: Int, title: String): MdEdit {
        val c = cursor.coerceIn(0, text.length)
        val query = typedQuery(text, c) ?: return MdEdit(text, c)
        val start = c - query.length
        val closed = text.startsWith("]]", c)
        val insert = if (closed) title else "$title]]"
        val t = text.substring(0, start) + insert + text.substring(c)
        return MdEdit(t, start + title.length + 2)
    }

    /** Les titres proposés pour [query] : ceux qui commencent par elle d'abord, puis ceux qui la contiennent. */
    fun suggest(titles: List<String>, query: String, exclude: String?, limit: Int = 6): List<String> {
        val q = key(query)
        val ex = exclude?.let(::key)
        val usable = titles.filter { it.isNotBlank() && key(it) != ex }.distinctBy(::key)
        val starts = usable.filter { key(it).startsWith(q) }
        val contains = usable.filter { key(it).contains(q) && !key(it).startsWith(q) }
        return (starts.sortedBy { it.length } + contains.sortedBy { it.length }).take(limit)
    }
}
